import { useEffect } from "react";
import { useAtom, useSetAtom } from "jotai";

import { getSocket, disconnectSocket } from "./socket.js";

import { 
  pregameAlertAtom, 
  clientAtom, 
  gamePhaseAtom,
  isOnlineAtom, 
  hasTurnAtom, 
  helperTilesAtom, 
  hostAtom, 
  initialYootThrowAtom, 
  legalTilesAtom, 
  messagesAtom, 
  particleSettingAtom, 
  pieceTeam0Id0Atom, 
  pieceTeam0Id1Atom, 
  pieceTeam0Id2Atom, 
  pieceTeam0Id3Atom, 
  pieceTeam1Id0Atom, 
  pieceTeam1Id1Atom, 
  pieceTeam1Id2Atom, 
  pieceTeam1Id3Atom, 
  readyToStartAtom, 
  roomAtom, 
  selectionAtom, spectatorsAtom, teamsAtom, tilesAtom, turnAtom, winnerAtom, yootActiveAtom, yootThrowValuesAtom, yootThrownAtom, moveResultAtom, throwResultAtom, throwAlertAtom, turnAlertActiveAtom, throwCountAtom, gameLogsAtom, yootAnimationAtom, 
  yootOutcomeAtom,
  currentPlayerNameAtom,
  alertsAtom,
  catchOutcomeAtom,
  catchPathAtom,
  settingsOpenAtom,
  pauseGameAtom,
  backdoLaunchAtom,
  timerAtom,
  nakAtom,
  yutMoCatchAtom,
  turnExpireTimeAtom,
  resultsAtom,
  turnStartTimeAtom,
  remainingTimeAtom,
  yootAnimationPlayingAtom,
  pieceTeam0Id0AnimationPlayingAtom,
  pieceTeam0Id1AnimationPlayingAtom,
  pieceTeam0Id2AnimationPlayingAtom,
  pieceTeam0Id3AnimationPlayingAtom,
  pieceTeam1Id0AnimationPlayingAtom,
  pieceTeam1Id1AnimationPlayingAtom,
  pieceTeam1Id2AnimationPlayingAtom,
  pieceTeam1Id3AnimationPlayingAtom,
  showFinishMovesAtom,
  showBonusAtom,
  bonusExistsAtom,
  lastYutAtom,
  shortcutOptionsAtom,
  hasAIAtom,
  blueMoonBrightnessAtom,
  audioVolumeAtom,
  minigameCountdownAtom,
  minigameDodgeFrameAtom,
  minigameGamePhaseAtom,
  minigameHideAndSeekCatchAtom,
  minigameHideAndSeekShotAtom,
  minigameMessageAtom,
  minigameOpponentShakeAtom,
  minigameScoresAtom,
  minigameStateAtom,
  minigameStarPopAtom,
  minigameStarsAtom,
  minigameTimeRemainingAtom,
  minigameRuleAtom,
} from "./GlobalState.jsx";
import { clientHasTurn, movesIsEmpty, pickRandomElement } from "./logicHelpers/helpers.js";
import * as THREE from 'three';
import initialState from "./dictionaries/initialState.js";
import useSoundEffectsPlayer from "./soundPlayers/useSoundEffectsPlayer.jsx";
import useStarRoll from "./shader/starRoll/StarRoll.jsx";
import { useLoader } from "@react-three/fiber";
import { TextureLoader } from 'three/src/loaders/TextureLoader'
import useMeteorsShader from "./shader/meteors/useMeteorsShader.jsx";
import { useGameHelpers } from "./stores/useGameHelpers.js";
import { getOccupiedTiles } from "./gameLogic/helpers.js";
import { onlyHasBackdo } from "./gameLogic/rules.js";

export const SocketManager = () => {
  // Socket is now lazy-loaded - initialized only when SocketManager mounts
  const socket = getSocket();
  const [client, setClient] = useAtom(clientAtom);
  const [teams, setTeams] = useAtom(teamsAtom)
  const [turn, setTurn] = useAtom(turnAtom);
  const [_room] = useAtom(roomAtom);
  const [_minigameStars, setMinigameStars] = useAtom(minigameStarsAtom)
  const setMinigameGamePhase = useSetAtom(minigameGamePhaseAtom)
  const setMinigameCountdown = useSetAtom(minigameCountdownAtom)
  const setMinigameScores = useSetAtom(minigameScoresAtom)
  const setMinigameTimeRemaining = useSetAtom(minigameTimeRemainingAtom)
  const setMinigameMessage = useSetAtom(minigameMessageAtom)
  const setMinigameOpponentShake = useSetAtom(minigameOpponentShakeAtom)
  const setMinigameStarPop = useSetAtom(minigameStarPopAtom)
  const setMinigameState = useSetAtom(minigameStateAtom)
  const setMinigameDodgeFrame = useSetAtom(minigameDodgeFrameAtom)
  const setMinigameHideAndSeekCatch = useSetAtom(minigameHideAndSeekCatchAtom)
  const setMinigameHideAndSeekShot = useSetAtom(minigameHideAndSeekShotAtom)
  const [_messages, setMessages] = useAtom(messagesAtom);
  const [_gameLogs, setGameLogs] = useAtom(gameLogsAtom);
  const [_host, setHost] = useAtom(hostAtom)
  const [_spectators, setSpectators] = useAtom(spectatorsAtom)
  const [_readyToStart, setReadyToStart] = useAtom(readyToStartAtom)
  const [_yootActive, setYootActive] = useAtom(yootActiveAtom)
  const [_yootThrowValues] = useAtom(yootThrowValuesAtom)
  const [_initialYootThrow] = useAtom(initialYootThrowAtom)
  const [_yootThrown] = useAtom(yootThrownAtom)
  const [hasTurn, setHasTurn] = useAtom(hasTurnAtom)
  const [_turnAlertActive] = useAtom(turnAlertActiveAtom)
  const [_moveResult] = useAtom(moveResultAtom)
  const [_throwResult] = useAtom(throwResultAtom)
  const [_pregameAlert] = useAtom(pregameAlertAtom)
  const [_throwAlert] = useAtom(throwAlertAtom)

  // Use state to check if the game phase changed
  const [_gamePhase, setGamePhase] = useAtom(gamePhaseAtom)
  const [_selection, setSelection] = useAtom(selectionAtom)
  const [_legalTiles, setLegalTiles] = useAtom(legalTilesAtom)
  const [_helperTiles, setHelperTiles] = useAtom(helperTilesAtom)
  const [_tiles, setTiles] = useAtom(tilesAtom)
  
  // Pieces on the board
  const [_pieceTeam0Id0, setPieceTeam0Id0] = useAtom(pieceTeam0Id0Atom)
  const [_pieceTeam0Id1, setPieceTeam0Id1] = useAtom(pieceTeam0Id1Atom)
  const [_pieceTeam0Id2, setPieceTeam0Id2] = useAtom(pieceTeam0Id2Atom)
  const [_pieceTeam0Id3, setPieceTeam0Id3] = useAtom(pieceTeam0Id3Atom)
  const [_pieceTeam1Id0, setPieceTeam1Id0] = useAtom(pieceTeam1Id0Atom)
  const [_pieceTeam1Id1, setPieceTeam1Id1] = useAtom(pieceTeam1Id1Atom)
  const [_pieceTeam1Id2, setPieceTeam1Id2] = useAtom(pieceTeam1Id2Atom)
  const [_pieceTeam1Id3, setPieceTeam1Id3] = useAtom(pieceTeam1Id3Atom)
  
  const setPieceTeam0Id0AnimationPlaying = useSetAtom(pieceTeam0Id0AnimationPlayingAtom)
  const setPieceTeam0Id1AnimationPlaying = useSetAtom(pieceTeam0Id1AnimationPlayingAtom)
  const setPieceTeam0Id2AnimationPlaying = useSetAtom(pieceTeam0Id2AnimationPlayingAtom)
  const setPieceTeam0Id3AnimationPlaying = useSetAtom(pieceTeam0Id3AnimationPlayingAtom)
  const setPieceTeam1Id0AnimationPlaying = useSetAtom(pieceTeam1Id0AnimationPlayingAtom)
  const setPieceTeam1Id1AnimationPlaying = useSetAtom(pieceTeam1Id1AnimationPlayingAtom)
  const setPieceTeam1Id2AnimationPlaying = useSetAtom(pieceTeam1Id2AnimationPlayingAtom)
  const setPieceTeam1Id3AnimationPlaying = useSetAtom(pieceTeam1Id3AnimationPlayingAtom)
  const [_throwCount, setThrowCount] = useAtom(throwCountAtom)
  const [_winner, setWinner] = useAtom(winnerAtom)
  // UI
  const [_particleSetting, setParticleSetting] = useAtom(particleSettingAtom)

  // animations
  const [_yootAnimation, setYootAnimation] = useAtom(yootAnimationAtom)
  const [_yootOutcome, setYootOutcome] = useAtom(yootOutcomeAtom)
  const [_currentPlayerName, setCurrentPlayerName] = useAtom(currentPlayerNameAtom)
  const [_alerts, setAlerts] = useAtom(alertsAtom)
  const [_catchOutcome] = useAtom(catchOutcomeAtom)
  const setYootAnimationPlaying = useSetAtom(yootAnimationPlayingAtom)
  const [_catchPath, setCatchPath] = useAtom(catchPathAtom);

  function applyMinigameState(minigame) {
    const nextMinigame = minigame ?? {}
    if (minigame == null) setMinigameHideAndSeekCatch(null)
    setMinigameState(nextMinigame)
    setMinigameStars(JSON.parse(JSON.stringify(nextMinigame.stars ?? initialState.initialMinigameStars)))
    setMinigameGamePhase(nextMinigame.phase ?? 'start')
    setMinigameCountdown(nextMinigame.countdown ?? null)
    setMinigameScores({
      0: nextMinigame.scores?.[0] ?? 0,
      1: nextMinigame.scores?.[1] ?? 0,
    })
    setMinigameTimeRemaining(nextMinigame.timeRemaining ?? 60)
    setMinigameMessage(nextMinigame.message ?? '')
    setMinigameOpponentShake({
      team: nextMinigame.opponentShakeTeam ?? null,
      expiresAt: nextMinigame.opponentShakeUntil ?? null,
      type: nextMinigame.opponentEffectType ?? null
    })
    if (minigame == null || nextMinigame.phase === 'countdown') {
      setMinigameStarPop(null)
    }
  }

  const setSettingsOpen = useSetAtom(settingsOpenAtom);
  const setPauseGame = useSetAtom(pauseGameAtom);
  // Game rules
  const setBackdoLaunch = useSetAtom(backdoLaunchAtom);
  const setTimer = useSetAtom(timerAtom)
  const setNak = useSetAtom(nakAtom)
  const setShortcutOptions = useSetAtom(shortcutOptionsAtom)
  const setYutMoCatch = useSetAtom(yutMoCatchAtom)
  const setMinigameRule = useSetAtom(minigameRuleAtom)
  const setTurnStartTime = useSetAtom(turnStartTimeAtom)
  const setTurnExpireTime = useSetAtom(turnExpireTimeAtom)
  const setRemainingTime = useSetAtom(remainingTimeAtom)
  const [results, setResults] = useAtom(resultsAtom)
  const setShowFinishMoves = useSetAtom(showFinishMovesAtom)
  const [bonusExists, setBonusExists] = useAtom(bonusExistsAtom)
  const setShowBonus = useSetAtom(showBonusAtom)
  const [lastYut, setLastYut] = useAtom(lastYutAtom)
  const [_hasAI, setHasAI] = useAtom(hasAIAtom)
  const setBlueMoonBrightness = useSetAtom(blueMoonBrightnessAtom)

  // sounds
  const { playSoundEffect } = useSoundEffectsPlayer()
  const setAudioVolume = useSetAtom(audioVolumeAtom)
  const setIsOnline = useSetAtom(isOnlineAtom)
  const [RollStar] = useStarRoll();
  const [CreateMeteor] = useMeteorsShader();

  // token choice
  const setTokenChoices = useGameHelpers(s => s.setTokenChoices)
  const setTokenChoice = useGameHelpers(s => s.setTokenChoice)

  useEffect(() => {
    setIsOnline(socket.connected)

    // Reconnect if disconnected when component mounts
    if (!socket.connected) {
      socket.connect()
    }

    socket.on('connect', () => {
      console.log('[SocketManager] connect')
      setIsOnline(true)
      setYootAnimationPlaying(false);
    })
    
    socket.on('connect_error', err => { 
      console.log('connect_error', err)
    })

    // Set client info in global store and local storage
    function findAndStoreClient(spectators, playersTeam0, playersTeam1) {

      for (const spectator of spectators) {
        if (spectator.socketId === socket.id) {
          setClient(spectator)
          localStorage.setItem('yootGame', JSON.stringify({
            ...spectator
          }))
          return
        }
      }

      for (const player of playersTeam0) {
        if (player.socketId === socket.id) {
          setClient(player)
          localStorage.setItem('yootGame', JSON.stringify({
            ...player
          }))
          return
        }
      }

      for (const player of playersTeam1) {
        if (player.socketId === socket.id) {
          setClient(player)
          localStorage.setItem('yootGame', JSON.stringify({
            ...player
          }))
          return
        }
      }
    }

    socket.on('room', (room) => {
      applyMinigameState(room.minigame)

      setMessages(room.messages)
      setTeams(room.teams)
      setSpectators(room.spectators)

      setPieceTeam0Id0(room.teams[0].pieces[0])
      setPieceTeam0Id1(room.teams[0].pieces[1])
      setPieceTeam0Id2(room.teams[0].pieces[2])
      setPieceTeam0Id3(room.teams[0].pieces[3])
      setPieceTeam1Id0(room.teams[1].pieces[0])
      setPieceTeam1Id1(room.teams[1].pieces[1])
      setPieceTeam1Id2(room.teams[1].pieces[2])
      setPieceTeam1Id3(room.teams[1].pieces[3])

      // Set host name for display
      if (room.host !== null) {
        setHost(room.host)
      }

      findAndStoreClient(room.spectators, room.teams[0].players, room.teams[1].players);

      setGamePhase((lastPhase) => {
        if (lastPhase === 'minigame') {
          return lastPhase
        }
        return room.gamePhase
      });

      const currentTeam = room.turn.team

      // Enable yoot button if client has the turn and his team 
      // has at least one throw and there is a player on the team
      // and the thrown flag is off
      if ((room.gamePhase === "pregame" || room.gamePhase === 'game') && (room.teams[currentTeam].players.length > 0 && 
      clientHasTurn(socket.id, room.teams, room.turn.team, room.turn.players[room.turn.team]) &&
      room.teams[currentTeam].throws > 0)) {
        setYootActive(true)
      } else {
        setYootActive(false)
      }

      // Enable 'Let's play' button
      if (room.gamePhase === 'lobby' && 
      room.teams[0].players.length > 0 && 
      room.teams[1].players.length > 0 &&
      allPlayersConnected(room.teams[0].players, room.teams[1].players)) {
        setReadyToStart(true)
      } else {
        setReadyToStart(false)
      }

      setTurn(room.turn)

      if ((room.gamePhase === "pregame" || room.gamePhase === "game") && 
      clientHasTurn(socket.id, room.teams, room.turn.team, room.turn.players[room.turn.team])) {

        setHasTurn(true)

      } else {

        setHasTurn(false)

      }

      setSelection(room.selection)
      setLegalTiles(room.legalTiles)
      let helperTiles = {}
      let legalTiles = room.legalTiles
      let tiles = room.tiles
      let selection = room.selection

      // helper tiles
      for (const legalTile of Object.keys(legalTiles)) {
        let moveInfo;
        if (legalTile !== '29') {
          moveInfo = legalTiles[legalTile]
          
          let helperProps = {
            move: parseInt(moveInfo.move)
          }

          // additional text
          if (tiles[legalTile].length === 0) { // if tile doesn't contain a piece
            helperProps.action = 'move'
          } else if (tiles[legalTile][0].team !== selection.pieces[0].team) {
            helperProps.action = 'catch'
          } else if (tiles[legalTile][0].team === selection.pieces[0].team) {
            helperProps.action = 'join'
          }
          helperTiles[legalTile] = helperProps
        }
      }

      setHelperTiles(helperTiles)

      setTiles(room.tiles)

      // result logic
      if (room.results.length === 0) {
        setWinner(-1)
      } else {
        setWinner(room.results[room.results.length-1])
      }

      if (room.gamePhase === 'pregame' || room.gamePhase === 'game') {
        const turn = room.turn
        setThrowCount(room.teams[turn.team].throws)
      }

      setGameLogs(room.gameLogs)

      setPauseGame(room.paused)

      setBackdoLaunch(room.rules.backdoLaunch)
      setTimer(room.rules.timer)
      setNak(room.rules.nak)
      setYutMoCatch(room.rules.yutMoCatch)
      setShortcutOptions(room.rules.shortcutOptions)
      setMinigameRule(room.rules.minigame ?? false)
      setTurnStartTime(room.turnStartTime)
      setTurnExpireTime(room.turnExpireTime)
      setAlerts([])
      if (room.paused) {
        setRemainingTime(room.turnExpireTime - room.pauseTime)
      } else {
        setRemainingTime(room.turnExpireTime - Date.now())
      }
      setResults(room.results);
      // don't set pieceAnimation or animation (alerts) playing so that
      // user can throw while timer is running

      // if selection
      // get token choices based on that.
      if (room.selection) {
        const legalTilesSet = new Set()
        for (const key in room.legalTiles) {
          legalTilesSet.add(parseInt(key))
        }
        // turn it into an array to iterate through them
        const legalTilesArray = Array.from(legalTilesSet)
        setTokenChoices(legalTilesArray)
        setTokenChoice(0)
      } else if (room.turn && room.turn.team && room.teams[room.turn.team]) {
        console.log('getting occupied tiles')
        const occupiedTiles = getOccupiedTiles({ pieces: room.teams[room.turn.team].pieces })
        if (onlyHasBackdo(room.teams[room.turn.team].moves)) {
          const homeTileIndex = occupiedTiles.indexOf(-1)
          if (homeTileIndex !== -1) {
            occupiedTiles.splice(homeTileIndex, 1)
          }
        }
        setTokenChoices(occupiedTiles)
        setTokenChoice(0)
      }
    })

    socket.on('teamNameUpdated', ({ team, name }) => {
      if (team !== 0 && team !== 1) return
      setTeams((teams) => teams.map((currentTeam, index) => (
        index === team ? { ...currentTeam, name } : currentTeam
      )))
    })

    socket.on('playerNameUpdated', ({ playerId, name }) => {
      if (!playerId || typeof name !== 'string') return
      setTeams((teams) => teams.map((team) => ({
        ...team,
        players: team.players.map((player) => player._id === playerId ? { ...player, name } : player)
      })))
      setClient((currentClient) => {
        if (currentClient._id !== playerId) return currentClient
        const updatedClient = { ...currentClient, name }
        localStorage.setItem('yootGame', JSON.stringify(updatedClient))
        return updatedClient
      })
    })

    socket.on('throwYut', ({ yootOutcome, yootAnimation, throwCount, turnExpireTime, paused }) => {
      setYootOutcome(yootOutcome)
      setYootAnimation(yootAnimation)
      setThrowCount(throwCount)
      setTurnExpireTime(turnExpireTime)
      setYootAnimationPlaying(true)
      setPauseGame(paused)
      if (throwCount < 1) {
        setBonusExists(false)
      }
      let soundPicker = Math.random()
      if (soundPicker < 0.5) {
        setTimeout(() => {
          setAudioVolume((volume) => {
            playSoundEffect('/sounds/effects/yut-sticks-0.mp3', volume)
            return volume
          })
        }, 1000)
      } else {
        setTimeout(() => {
          setAudioVolume((volume) => {
            playSoundEffect('/sounds/effects/yut-sticks-1.mp3', volume)
            return volume
          })
        }, 1000)
      }

      // set timeout
      // depending on game phase
        // pregame: pass turn
        // game: pass turn, set num throws, set moves
        // play alerts
        // make AI play // add handler
    })

    socket.on('gameStart', ({ gamePhase, newTeam, newPlayer, throwCount, turnStartTime, turnExpireTime, newGameLog, timer, hasAI }) => {
      setGamePhase(gamePhase)
      setTurn(turn => {
        turn.team = newTeam;
        turn.players[turn.team] = newPlayer
        return { ...turn } // must spread the object
      })
      setTeams(teams => {
        const newTeamObj = { ...teams[newTeam] }
        newTeamObj.throws = throwCount
        teams[newTeam] = newTeamObj
        return [...teams];
      })
      setThrowCount(throwCount)
      setAlerts(['gameStart', 'turn'])
      setYootOutcome(null)
      setYootAnimation(null)
      setYootAnimationPlaying(false)
      setTurnStartTime(turnStartTime)
      setTurnExpireTime(turnExpireTime)
      setRemainingTime(turnExpireTime - turnStartTime)
      setGameLogs(gameLogs => [...gameLogs, newGameLog])
      setBonusExists(false)
      setTimer(timer)
      setHasAI(hasAI)
      setTokenChoices([]) // clear token choices to prevent confusion, will be reloaded in 'passTurn' handler
      setTokenChoice(null)
      // test
      // setClient({ team: 0 })
      // setGamePhase('finished')
      // setWinner(1)
    })

    socket.on('passTurn', ({ newTeam, newPlayer, throwCount, turnStartTime, turnExpireTime, content, newGameLogs, gamePhase, paused }) => {
      setTurn(turn => {
        turn.team = newTeam;
        turn.players[turn.team] = newPlayer
        return { ...turn } // must spread the object
      })
      
      let alerts = []
      // if (!paused) {
      //   alerts.push('timesUp')
      // }

      setTeams(teams => {
        const newTeamObj = { ...teams[newTeam] }
        newTeamObj.throws = throwCount
        const prevTeam = content.prevTeam
        let prevTeamObj = { ...teams[prevTeam] }
        if (content.pregameOutcome) {
          if (content.pregameOutcome === 'tie') {
            prevTeamObj.pregameRoll = null
            newTeamObj.pregameRoll = null
            alerts.push('pregameTie')
          } else if (content.pregameOutcome === 'pass') { // pass or winner decided
            prevTeamObj.pregameRoll = 0
          } else {
            prevTeamObj.pregameRoll = 0
            if (prevTeam === 0) {
              alerts.push('pregameUfosWin')
            } else {
              alerts.push('pregameRocketsWin')
            }
          }
        } else {
          prevTeamObj.moves = JSON.parse(JSON.stringify(initialState.initialMoves))
          prevTeamObj.throws = 0
        }
        teams[prevTeam] = prevTeamObj
        teams[newTeam] = newTeamObj
        return [...teams];
      })

      if (!paused) {
        alerts.push('turn')
        setAlerts(alerts)

        setYootOutcome(null)
      }
      setGamePhase(gamePhase)
      setThrowCount(throwCount)
      setTurnStartTime(turnStartTime)
      setTurnExpireTime(turnExpireTime)
      setGameLogs(gameLogs => [...gameLogs, ...newGameLogs])
      setSelection(null)
      setLegalTiles({})
      setHelperTiles({})
      setPauseGame(paused)
    })

    socket.on('recordThrow', ({ teams, gamePhaseUpdate, turnUpdate, pregameOutcome, yootOutcome, newGameLogs, turnStartTime, turnExpireTime, paused, minigame }) => {
      const minigamePending = minigame?.phase === 'pending'
      applyMinigameState(minigame)
      setTeams(teams) // only update the throw count of the current team

      // this invocation is within a useEffect
      // 'gamePhase' state is saved as the one loaded in component load because there's no dependency
      let gamePhasePrev;
      setGamePhase((prev) => {
        gamePhasePrev = prev;
        return gamePhaseUpdate
      })
      let turnPrev;
      setTurn((prev) => {
        turnPrev = prev;
        return turnUpdate
      })

      if (teams[turnUpdate.team].players.length > 0) {
        const currentPlayerName = teams[turnUpdate.team].players[turnUpdate.players[turnUpdate.team]].name
        setCurrentPlayerName(currentPlayerName)
      } else {
        setCurrentPlayerName('')
      }

      if (gamePhaseUpdate === 'pregame') {
        let yootOutcomeAlertName;
        if (yootOutcome === 4 || yootOutcome === 5) {
          yootOutcomeAlertName = `yootOutcome${yootOutcome}Pregame`
        } else {
          yootOutcomeAlertName = `yootOutcome${yootOutcome}`
        }
        if (pregameOutcome === 'pass') {
          let alerts = [yootOutcomeAlertName]
          teams[turnUpdate.team].players.length > 0 && alerts.push('turn')
          setAlerts(alerts)
          
          setYootOutcome(null)
          setThrowCount(teams[turnUpdate.team].throws)
        } else if (pregameOutcome === 'tie') {
          let alerts = [yootOutcomeAlertName, 'pregameTie']
          teams[turnUpdate.team].players.length > 0 && alerts.push('turn')
          setAlerts(alerts)

          setYootOutcome(null)
          setThrowCount(teams[turnUpdate.team].throws)
        }
      } else if (gamePhasePrev === 'pregame' && gamePhaseUpdate === 'game') {
        let yootOutcomeAlertName;
        if (yootOutcome === 4 || yootOutcome === 5) {
          yootOutcomeAlertName = `yootOutcome${yootOutcome}Pregame`
        } else {
          yootOutcomeAlertName = `yootOutcome${yootOutcome}`
        }
        if (pregameOutcome === '0') { // changes from int to string
          let alerts = [yootOutcomeAlertName, 'pregameRocketsWin']
          teams[turnUpdate.team].players.length > 0 && alerts.push('turn')
          setAlerts(alerts)

          setYootOutcome(null)
          setThrowCount(teams[turnUpdate.team].throws)
        } else if (pregameOutcome === '1') {
          let alerts = [yootOutcomeAlertName, 'pregameUfosWin']
          teams[turnUpdate.team].players.length > 0 && alerts.push('turn')
          setAlerts(alerts)

          setYootOutcome(null)
          setThrowCount(teams[turnUpdate.team].throws)
        }
      } else if (gamePhaseUpdate === 'game') {
        let yootOutcomeAlertName = `yootOutcome${yootOutcome}`
        // yootOutcome === -1 doesn't need to check for backdoLaunch rule
        // because turn must have passed with backdoLaunch off if -1 is thrown
        if ((yootOutcome === 0 || yootOutcome === -1) && teams[turnPrev.team].throws === 0 && movesIsEmpty(teams[turnPrev.team].moves)) {
          if (yootOutcome === -1) {
            yootOutcomeAlertName += 'skip'
          }
          let alerts = [yootOutcomeAlertName] // add 'no available moves' alert
          if (teams[turnUpdate.team].players.length > 0) {
            if (!minigamePending) {
              alerts.push('turn')
            } else {
              alerts.push('minigameTime')
            }
          }
          setAlerts(alerts)

          setYootOutcome(null)
          // server determines if turn was skipped
        } else {
          setAlerts([yootOutcomeAlertName])
          
          // show token choices
          // it is a list of tile indeces in increasing order
          // control showing the command center in the yut component
          // if throws > 0, show throw button
          // else, if you have a move, show command center
          // else, it's not your turn
          const occupiedTiles = getOccupiedTiles({ pieces: teams[turnUpdate.team].pieces })
          
          if (onlyHasBackdo(teams[turnUpdate.team].moves)) {
            const homeTileIndex = occupiedTiles.indexOf(-1)
            if (homeTileIndex !== -1) {
              occupiedTiles.splice(homeTileIndex, 1)
            }
          }
          setTokenChoices(occupiedTiles)
          setTokenChoice(0)
        }
        setThrowCount(teams[turnUpdate.team].throws)
        if (teams[turnUpdate.team].throws > 0 && turnUpdate.team === turnPrev.team) {
          setBonusExists(true)
        } else {
          setBonusExists(false)
        }

        // meteor effect (alert)
        // color
        // speed
        // size
        // count
        if (yootOutcome === 4) {
          const count0 = 16
          // big stars
          for (let i = 0; i < count0; i++) {
          
              // need a THREE.Vector3 for position
              const position = new THREE.Vector3(
                  (Math.random() - 0.5) * 15 + 4.0, 
                  -5.0,
                  (Math.random() - 0.5) * 15 - 4.0, 
              )
              
              const rotation = new THREE.Vector3(
                  Math.PI/3, 
                  0,
                  0, 
              )
              
              const omitFactor = 4
              const size = 0.001 // particle size
              const scale = 0.4 + Math.random() * 0.7 // star size
              const color = new THREE.Color();
              color.setHSL(0.5 + Math.random() * 0.05, 1, 0.5);
              const duration = 2.0
              const speedX = -(8 + Math.random() * 4)
              const speedY = 4 + Math.random() * 2
              setTimeout(() => {
                RollStar({ position, rotation, duration, omitFactor, size, scale, color, speedX, speedY, brightness: 1.0 })
              }, i * 50)
          }
          // small stars
          // reduced omit count to make them less brighter
          // delayed start
          const count1 = 16
          for (let i = 0; i < count1; i++) {
          
              // need a THREE.Vector3 for position
              const position = new THREE.Vector3(
                  (Math.random() - 0.5) * 15 + 4.0, 
                  -5.0,
                  (Math.random() - 0.5) * 15 - 4.0, 
              )
              
              const rotation = new THREE.Vector3(
                  Math.PI/3, 
                  0,
                  0, 
              )
              
              const omitFactor = 6
              const size = 0.0008 // particle size
              const scale = 0.1 + Math.random() * 0.3 // star size
              const color = new THREE.Color();
              color.setHSL(0.5 + Math.random() * 0.05, 1, 0.5);
              const duration = 2.0
              const speedX = -(8 + Math.random() * 4)
              const speedY = 4 + Math.random() * 2
              setTimeout(() => {
                RollStar({ position, rotation, duration, omitFactor, size, scale, color, speedX, speedY, brightness: 1.0 })
              }, i * 50 + 200)
          }
        } else if (yootOutcome === 5) {
          // shiny rolling stars
          const count0 = 16
          // big stars
          for (let i = 0; i < count0; i++) {
          
              // need a THREE.Vector3 for position
              const position = new THREE.Vector3(
                  (Math.random() - 0.5) * 15 + 4, 
                  -5.0,
                  (Math.random() - 0.5) * 15 - 4.0, 
              )
              
              const rotation = new THREE.Vector3(
                  Math.PI/3, 
                  0,
                  0, 
              )
              
              const omitFactor = 4
              const size = 0.001 // particle size
              const scale = 0.4 + Math.random() * 0.7 // star size
              const color = new THREE.Color();
              color.setHSL(0.5 + Math.random() * 0.05, 1, 0.5);
              const duration = 2.0
              const speedX = -(8 + Math.random() * 4)
              const speedY = 4 + Math.random() * 2
              setTimeout(() => {
                  RollStar({ position, rotation, duration, omitFactor, size, scale, color, speedX, speedY, shiny: true, brightness: 1.0 })
              }, i * 50)
          }
          // small stars
          // reduced omit count to make them less brighter
          // delayed start
          const count1 = 16
          for (let i = 0; i < count1; i++) {
          
              // need a THREE.Vector3 for position
              const position = new THREE.Vector3(
                  (Math.random() - 0.5) * 15 + 4, 
                  -5.0,
                  (Math.random() - 0.5) * 15 - 4.0, 
              )
              
              const rotation = new THREE.Vector3(
                  Math.PI/3, 
                  0,
                  0, 
              )
              
              const omitFactor = 6
              const size = 0.0008 // particle size
              const scale = 0.1 + Math.random() * 0.3 // star size
              const color = new THREE.Color();
              color.setHSL(0.5 + Math.random() * 0.05, 1, 0.5);
              const duration = 2.0
              const speedX = -(8 + Math.random() * 4)
              const speedY = 4 + Math.random() * 2
              setTimeout(() => {
                  RollStar({ position, rotation, duration, omitFactor, size, scale, color, speedX, speedY, shiny: true, brightness: 1.0 })
              }, i * 50 + 200)
          }
          // meteors
          const meteorCount = 9
          for (let i = 0; i < meteorCount; i++) {
              setTimeout(() => {
                const count = Math.round(400 + Math.random() * 1000);
                const position = new THREE.Vector3(
                    (Math.random()-0.5) * 7 + 5, 
                    -5.0,
                    (Math.random()-0.5) * 10 - 4.0, 
                )
                const size = 0.15 + Math.random() * 0.1
                const texture = useLoader(TextureLoader, 'textures/particles/3.png')
                // color is determined in the fragment shader with the burn progress
                const color = new THREE.Color();
                const speedX = 4.0 + Math.random() * 1.0
                const speedY = 2.0 + Math.random() * 0.6
                color.setHSL(1.0, 1.0, 1.0)
                CreateMeteor({
                  count,
                  position,
                  size,
                  texture,
                  color,
                  speedX,
                  speedY
                })
              }, i * 50)
          }
        }
      }

      setTurnStartTime(turnStartTime)
      setTurnExpireTime(turnExpireTime)
      setYootAnimationPlaying(false)
      setYootAnimation(null)
      setHasTurn(clientHasTurn(socket.id, teams, turnUpdate.team, turnUpdate.players[turnUpdate.team]))
      setGameLogs(gameLogs => [...gameLogs, ...newGameLogs])
      setPauseGame(paused)

      // Sounds
      if (yootOutcome === 1) {
        const doSoundFilePaths = [
          '/sounds/effects/yutThrows/produced/do-strong.mp3',
          '/sounds/effects/yutThrows/produced/do!.mp3',
          '/sounds/effects/yutThrows/produced/do.mp3'
        ]
        // playSoundEffect(pickRandomElement(doSoundFilePaths))
      } else if (yootOutcome === 2) {
        const geSoundFilePaths = [
          '/sounds/effects/yutThrows/produced/ge-strong.mp3',
          '/sounds/effects/yutThrows/produced/ge.mp3',
          '/sounds/effects/yutThrows/produced/개다.mp3',
          '/sounds/effects/yutThrows/produced/개애애애애.mp3',
        ]
        // playSoundEffect(pickRandomElement(geSoundFilePaths))
      } else if (yootOutcome === 3) {
        const gulSoundFilePaths = [
          '/sounds/effects/yutThrows/produced/gul-gul.mp3',
          '/sounds/effects/yutThrows/produced/gul-strong.mp3',
          '/sounds/effects/yutThrows/produced/gul.mp3',
          '/sounds/effects/yutThrows/produced/걸이다.mp3',
          '/sounds/effects/yutThrows/produced/걸이로구나.mp3',
        ]
        // playSoundEffect(pickRandomElement(gulSoundFilePaths))
      } else if (yootOutcome === 4) {
        const yutSoundFilePaths = [
          '/sounds/effects/yutThrows/produced/yut!.mp3',
          '/sounds/effects/yutThrows/produced/yuuuuuuuut.mp3',
        ]
        setAudioVolume((volume) => {
          playSoundEffect(pickRandomElement(yutSoundFilePaths), volume)
          return volume
        })
      } else if (yootOutcome === 5) {
        const moSoundFilePaths = [
          '/sounds/effects/yutThrows/produced/mo!.mp3',
          '/sounds/effects/yutThrows/produced/mooooooo.mp3',
        ]
        setAudioVolume((volume) => {
          playSoundEffect(pickRandomElement(moSoundFilePaths), volume)
          return volume
        })
      } else if (yootOutcome === -1) {
        const backdoSoundFilePaths = [
          '/sounds/effects/yutThrows/produced/nak-chimes.mp3',
          // '/sounds/effects/yutThrows/produced/ditto-confused.mp3',
          // '/sounds/effects/yutThrows/produced/ditto-deep.mp3',
          // '/sounds/effects/yutThrows/produced/ditto-short.mp3',
          // '/sounds/effects/yutThrows/produced/ditto~.mp3',
        ]
        setAudioVolume((volume) => {
          playSoundEffect(pickRandomElement(backdoSoundFilePaths), volume)
          return volume
        })
      } else if (yootOutcome === 0) {
        const nakSoundFilePaths = [
          '/sounds/effects/yutThrows/produced/backdo.mp3',
        ]
        setAudioVolume((volume) => {
          playSoundEffect(pickRandomElement(nakSoundFilePaths), volume)
          return volume
        })
      }
    })

    socket.on('move', ({ newTeam, prevTeam, newPlayer, moveUsed, moveResult, updatedPieces, updatedTiles, throws, newGameLogs, turnStartTime, turnExpireTime, paused, minigame }) => {
      // instead of teamsUpdate and tiles, receive updatedPieces and updatedTiles
      // check if moves is empty. if it is, clear it
      // clear legalTiles and selection

      let alerts = []
      const minigamePending = minigame?.phase === 'pending'
      applyMinigameState(minigame)
      if (moveResult?.type === 'join') {
        alerts.push(`join${moveResult.team}${moveResult.tile}`)
      }

      let piecesCurrentTeam = []
      let piecesEnemyTeam = []
      setTeams(teams => {
        // Update pieces
        for (const piece of updatedPieces) {
          teams[piece.team].pieces[piece.id] = piece
          teams[piece.team].pieces[piece.id].history = [...piece.history]
          teams[piece.team].pieces[piece.id].lastPath = [...piece.lastPath]
          if (piece.team === 0 && piece.id === 0) {
            setPieceTeam0Id0(piece)
            setPieceTeam0Id0AnimationPlaying(true)
          }
          else if (piece.team === 0 && piece.id === 1) {
            setPieceTeam0Id1(piece)
            setPieceTeam0Id1AnimationPlaying(true)
          }
          else if (piece.team === 0 && piece.id === 2) {
            setPieceTeam0Id2(piece)
            setPieceTeam0Id2AnimationPlaying(true)
          }
          else if (piece.team === 0 && piece.id === 3) {
            setPieceTeam0Id3(piece)
            setPieceTeam0Id3AnimationPlaying(true)
          }
          else if (piece.team === 1 && piece.id === 0) {
            setPieceTeam1Id0(piece)
            setPieceTeam1Id0AnimationPlaying(true)
          }
          else if (piece.team === 1 && piece.id === 1) {
            setPieceTeam1Id1(piece)
            setPieceTeam1Id1AnimationPlaying(true)
          }
          else if (piece.team === 1 && piece.id === 2) {
            setPieceTeam1Id2(piece)
            setPieceTeam1Id2AnimationPlaying(true)
          }
          else if (piece.team === 1 && piece.id === 3) {
            setPieceTeam1Id3(piece)
            setPieceTeam1Id3AnimationPlaying(true)
          }

          // for catch
          if (newTeam === prevTeam) {
            if (piece.team === newTeam) 
              piecesCurrentTeam.push(piece)
            else
              piecesEnemyTeam.push(piece)
          }
        }
        
        // Update throws
        teams[newTeam].throws = throws
        setThrowCount(throws)
        if (throws > 0 && newTeam === prevTeam) {
          setBonusExists(true)
        } else {
          setBonusExists(false)
        }

        // Update moves
        let newMoves
        if (newTeam !== prevTeam) {
          teams[prevTeam].moves = JSON.parse(JSON.stringify(initialState.initialMoves))
        } else {
          teams[prevTeam].moves[moveUsed]--
        }
        newMoves = JSON.parse(JSON.stringify(teams[prevTeam].moves))
        
      
        // Update current player's name
        if (teams[newTeam].players[newPlayer].name) {
          const currentPlayerName = teams[newTeam].players[newPlayer].name
          setCurrentPlayerName(currentPlayerName)
        }

        // server handles backdo skip with backdoLaunch rule
        // if team changed: clear token choices
        // else: re-calculate token choices
        if (newTeam !== prevTeam) {
          setTokenChoices([])
          setTokenChoice(null)
        } else {
          const occupiedTiles = getOccupiedTiles({ pieces: teams[newTeam].pieces })
          // remove -1 (tile) if there's a token on the board and moves only contains backdo
          if (onlyHasBackdo(newMoves)) {
            const homeTileIndex = occupiedTiles.indexOf(-1)
            if (homeTileIndex !== -1) {
              occupiedTiles.splice(homeTileIndex, 1)
            }
          }
          setTokenChoices(occupiedTiles)
          setTokenChoice(0)
        }

        return [...teams]
      })

      setTiles(tiles => {
        const to = updatedTiles.to.index
        let newTiles = [...tiles]
        const from = updatedTiles.from.index
        newTiles[to] = []
        for (let i = 0; i < updatedTiles.to.pieces.length; i++) {
          const newPiece = {
            ...updatedTiles.to.pieces[i],
            history: updatedTiles.to.pieces[i].history,
            lastPath: updatedTiles.to.pieces[i].lastPath
          }
          newTiles[to].push(newPiece)
        }

        if (from !== -1) // true if piece is starting
          newTiles[from] = []
        return newTiles
      })
      
      // Catch
      if (piecesEnemyTeam.length > 0) {
        const enemyTeamId = piecesEnemyTeam[0].team
        alerts.push(`catch${enemyTeamId}${piecesEnemyTeam.length}${moveUsed}`)
        setCatchPath(newGameLogs[newGameLogs.length-1].content.path)
      }

      setTurn((turn) => {
        let newTurn = {
          team: newTeam,
          players: [...turn.players]
        }
        if (!minigamePending && turn.team !== newTeam) {
          alerts.push('turn')

          setYootOutcome(null)
        }
        newTurn.players[newTeam] = newPlayer
        return newTurn
      })

      // Sound for Start
      // Sound for Move
      // Sound for Catch
      // Sound for Piggyback

      if (minigamePending) {
        alerts.push('minigameTime')
      }
      setAlerts(alerts)
      // Turn could have changed
      console.log('minigamePending', minigamePending)
      setHasTurn(clientHasTurn(socket.id, teams, newTeam, newPlayer))
      setLegalTiles({})
      setHelperTiles({})
      setSelection(null)
      setGameLogs(gameLogs => [...gameLogs, ...newGameLogs])
      setTurnStartTime(turnStartTime)
      setTurnExpireTime(turnExpireTime)
      setPauseGame(paused)
    })

    socket.on('score', ({ newTeam, prevTeam, newPlayer, moveUsed, updatedPieces, from, throws, winner, gamePhase, newGameLogs, turnStartTime, turnExpireTime, paused, minigame }) => {
      const minigamePending = minigame?.phase === 'pending'
      applyMinigameState(minigame)
      setTeams(teams => {
        // Update pieces
        for (const piece of updatedPieces) {
          teams[piece.team].pieces[piece.id] = piece
          teams[piece.team].pieces[piece.id].history = [...piece.history]
          teams[piece.team].pieces[piece.id].lastPath = [...piece.lastPath]
          if (piece.team === 0 && piece.id === 0) {
            setPieceTeam0Id0(piece)
            setPieceTeam0Id0AnimationPlaying(true)
          }
          else if (piece.team === 0 && piece.id === 1) {
            setPieceTeam0Id1(piece)
            setPieceTeam0Id1AnimationPlaying(true)
          }
          else if (piece.team === 0 && piece.id === 2) {
            setPieceTeam0Id2(piece)
            setPieceTeam0Id2AnimationPlaying(true)
          }
          else if (piece.team === 0 && piece.id === 3) {
            setPieceTeam0Id3(piece)
            setPieceTeam0Id3AnimationPlaying(true)
          }
          else if (piece.team === 1 && piece.id === 0) {
            setPieceTeam1Id0(piece)
            setPieceTeam1Id0AnimationPlaying(true)
          }
          else if (piece.team === 1 && piece.id === 1) {
            setPieceTeam1Id1(piece)
            setPieceTeam1Id1AnimationPlaying(true)
          }
          else if (piece.team === 1 && piece.id === 2) {
            setPieceTeam1Id2(piece)
            setPieceTeam1Id2AnimationPlaying(true)
          }
          else if (piece.team === 1 && piece.id === 3) {
            setPieceTeam1Id3(piece)
            setPieceTeam1Id3AnimationPlaying(true)
          }
        }

        // Update throws
        teams[newTeam].throws = throws
        setThrowCount(throws)
        if (throws > 0 && newTeam === prevTeam) {
          setBonusExists(true)
        } else {
          setBonusExists(false)
        }

        // Update moves
        let newMoves
        if (newTeam !== prevTeam) {
          teams[prevTeam].moves = JSON.parse(JSON.stringify(initialState.initialMoves))
        } else {
          teams[prevTeam].moves[moveUsed]--
        }
        newMoves = JSON.parse(JSON.stringify(teams[prevTeam].moves))
        
        // Update current player's name
        const currentPlayerName = teams[newTeam].players[newPlayer].name
        setCurrentPlayerName(currentPlayerName)

        // server handles backdo skip with backdoLaunch rule
        // if team changed: clear token choices
        // else: re-calculate token choices
        if (newTeam !== prevTeam) {
          setTokenChoices([])
          setTokenChoice(null)
        } else {
          const occupiedTiles = getOccupiedTiles({ pieces: teams[newTeam].pieces })
          // remove -1 (tile) if there's a token on the board and moves only contains backdo
          if (onlyHasBackdo(newMoves)) {
            const homeTileIndex = occupiedTiles.indexOf(-1)
            if (homeTileIndex !== -1) {
              occupiedTiles.splice(homeTileIndex, 1)
            }
          }
          setTokenChoices(occupiedTiles)
          setTokenChoice(0)
        }

        return [...teams]
      })
      
      // Update tiles
      setTiles(tiles => {
        tiles[from] = []
        return [...tiles]
      })
      
      if (gamePhase !== 'finished') {
        const alerts = []
        let numPiecesScored = updatedPieces.length
        alerts.push(`score${prevTeam}${numPiecesScored}`)
  
        if (newTeam !== prevTeam) {
          setTurn(turn => {
            let newTurn = {
              team: newTeam,
              players: [...turn.players]
            }
            newTurn.players[newTeam] = newPlayer
            return newTurn
          })
          alerts.push('turn')
          setYootOutcome(null)
        }
        
        if (minigamePending) {
          alerts.push('minigameTime')
        }
        setAlerts(alerts)
      } else if (gamePhase === 'finished') {
        setResults(results => [ ...results, winner ])
        applyMinigameState(null)
        setPieceTeam0Id0AnimationPlaying(false)
        setPieceTeam0Id1AnimationPlaying(false)
        setPieceTeam0Id2AnimationPlaying(false)
        setPieceTeam0Id3AnimationPlaying(false)
        setPieceTeam1Id0AnimationPlaying(false)
        setPieceTeam1Id1AnimationPlaying(false)
        setPieceTeam1Id2AnimationPlaying(false)
        setPieceTeam1Id3AnimationPlaying(false)
      }

      setLegalTiles({})
      setHelperTiles({})

      setSelection(null)
      setGameLogs(gameLogs => [ ...gameLogs, ...newGameLogs ])
      setGamePhase(gamePhase)
      setWinner(winner)
      setTurnStartTime(turnStartTime)
      setTurnExpireTime(turnExpireTime)
      setPauseGame(paused)
      setShowFinishMoves(false)
    })

    socket.on('select', ({ selection, legalTiles }) => { //receive
      // handle
      setSelection(selection)
      setLegalTiles(legalTiles)
      // calculate helper tile in server to prevent passing 'tiles'
      
      let helperTiles = {}

      for (const legalTile of Object.keys(legalTiles)) {
        if (legalTile !== '29') {
          let moveInfo = legalTiles[legalTile]
          helperTiles[legalTile] = parseInt(moveInfo.move)
        }
      }



      setHelperTiles(helperTiles)

      if (selection === null) {
        setShowFinishMoves(false)
        setAudioVolume((volume) => {
          playSoundEffect('/sounds/effects/deselect.mp3', volume)
          return volume
        })
      } else {
        if (selection.pieces[0].team === 0) {
          // setAudioVolume((volume) => {
          //   playSoundEffect('/sounds/effects/star-highlight.mp3', volume)
          //   return volume
          // })
        } else if (selection.pieces[0].team === 1) {
          // setAudioVolume((volume) => {
          //   playSoundEffect('/sounds/effects/star-highlight.mp3', volume)
          //   return volume
          // })
        }
      }
    })

    // Emitted to other clients when a client joins
    // refactor: only players arrays from teams
    socket.on("joinRoom", ({ spectators, teams, host, gamePhase }) => {
      setSpectators(spectators);
      setTeams(teams);
      setHost(host);
      setGamePhase(gamePhase)
      console.log('gamePhase on joinRoom:', gamePhase)

      findAndStoreClient(spectators, teams[0].players, teams[1].players)
      
      if (gamePhase === 'lobby' && 
        teams[0].players.length > 0 && 
        teams[1].players.length > 0 &&
        allPlayersConnected(teams[0].players, teams[1].players)) {
          setReadyToStart(true)
        } else {
          setReadyToStart(false)
        }
    })
    
    socket.on("joinTeam", ({ spectators, playersTeam0, playersTeam1, gamePhase, host, turn, playerType }) => {
      setSpectators(spectators)
      setTeams((teams) => {
        let newTeams = [...teams]
        newTeams[0].players = []
        for (const player of playersTeam0) {
          newTeams[0].players.push({
            ...player
          })
        }
        newTeams[1].players = []
        for (const player of playersTeam1) {
          newTeams[1].players.push({
            ...player
          })
        }
        return newTeams
      });
      setHost(host);
      if (gamePhase === 'pregame' || gamePhase === 'game') {
        setThrowCount(teams[turn.team].throws)
      }
      
      findAndStoreClient(spectators, playersTeam0, playersTeam1)
      
      // Player could have joined an empty team
      if (gamePhase === 'pregame' || gamePhase === 'game') {
        let currentPlayerName
        if (currentTeam === 0) {
          currentPlayerName = playersTeam0[turn.players[currentTeam]].name
        } else if (currentTeam === 1) {
          currentPlayerName = playersTeam1[turn.players[currentTeam]].name
        }
        setCurrentPlayerName(currentPlayerName)
      }
      
      if (gamePhase === 'lobby' && 
        playersTeam0.length > 0 && 
        playersTeam1.length > 0 &&
        allPlayersConnected(playersTeam0, playersTeam1)) {
          setReadyToStart(true)
        } else {
          setReadyToStart(false)
        }

      // if (playerType === 'human') {
      //   playSoundEffect('/sounds/effects/door-chime.mp3')
      // } else if (playerType === 'ai') {
      //   playSoundEffect('/sounds/effects/add-ai-player.mp3')
      // }
    })

    socket.on('reset', () => {
      setGamePhase('lobby');
      setTiles(initialState.initialTiles);
      setTurn(initialState.initialTurn);
      applyMinigameState(null)
      setLegalTiles({})
      setSelection(null)

      setTeams((teams) => {
        const newTeams = [...teams] // make shallow copy
        newTeams[0].pieces = JSON.parse(JSON.stringify(initialState.initialTeams[0].pieces))
        newTeams[0].throws = 0
        newTeams[0].moves = JSON.parse(JSON.stringify(initialState.initialTeams[0].moves)),
        newTeams[0].pregameRoll = null
        newTeams[1].pieces = JSON.parse(JSON.stringify(initialState.initialTeams[1].pieces))
        newTeams[1].throws = 0
        newTeams[1].moves = JSON.parse(JSON.stringify(initialState.initialTeams[1].moves)),
        newTeams[1].pregameRoll = null
        
        // use previous teams state because it's not updated
        if (teams[0].players.length > 0 && 
          teams[1].players.length > 0 &&
          allPlayersConnected(teams[0].players, teams[1].players)) {
            setReadyToStart(true)
          } else {
            setReadyToStart(false)
          }

        return newTeams;
      })

      setPieceTeam0Id0(JSON.parse(JSON.stringify(initialState.initialTeams[0].pieces[0])))
      setPieceTeam0Id1(JSON.parse(JSON.stringify(initialState.initialTeams[0].pieces[1])))
      setPieceTeam0Id2(JSON.parse(JSON.stringify(initialState.initialTeams[0].pieces[2])))
      setPieceTeam0Id3(JSON.parse(JSON.stringify(initialState.initialTeams[0].pieces[3])))
      setPieceTeam1Id0(JSON.parse(JSON.stringify(initialState.initialTeams[1].pieces[0])))
      setPieceTeam1Id1(JSON.parse(JSON.stringify(initialState.initialTeams[1].pieces[1])))
      setPieceTeam1Id2(JSON.parse(JSON.stringify(initialState.initialTeams[1].pieces[2])))
      setPieceTeam1Id3(JSON.parse(JSON.stringify(initialState.initialTeams[1].pieces[3])))

      setTurnExpireTime(null)
      setBlueMoonBrightness(null)
      setYootAnimation(null)
      setYootAnimationPlaying(false)
    })

    socket.on('minigameStarsInitialized', ({ stars, gamePhase }) => {
      if (!Array.isArray(stars)) {
        return
      }

      setMinigameStars(JSON.parse(JSON.stringify(stars)))

      if (typeof gamePhase === 'string' && gamePhase.length > 0) {
        setGamePhase(gamePhase)
      }
    })

    socket.on('minigameState', ({ gamePhase, minigame }) => {
      if (typeof gamePhase === 'string' && gamePhase.length > 0) {
        setGamePhase(gamePhase)
      }

      applyMinigameState(minigame)

      console.log('minigameState received:', minigame)
      if (minigame && minigame.phase === 'pending') {
        setAlerts(['minigameTime'])
      }

      // UI update
      if (minigame && minigame.gamePhase === 'countdown') {
        // clear star popped
        setMinigameStarPop(null)
      }
    })

    socket.on('minigameStarPopped', (starPop) => {
      setMinigameStarPop(starPop)
    })

    socket.on('minigameDodgeFrame', (frame) => {
      if (frame?.players) setMinigameDodgeFrame(frame)
    })

    socket.on('minigameHideAndSeekCatch', (catchEvent) => {
      setMinigameHideAndSeekCatch(catchEvent ?? null)
    })

    socket.on('minigameHideAndSeekShot', (shotEvent) => {
      setMinigameHideAndSeekShot(shotEvent ?? null)
    })

    socket.on('minigameTime', () => {
    })

    socket.on('minigameReturnToGame', ({ gamePhase, enhancedYutTeam }) => {
      if (typeof gamePhase === 'string' && gamePhase.length > 0) {
        setGamePhase(gamePhase)
      }
      if (gamePhase === 'game') {
        setAlerts(['turn'])
      }
      if (enhancedYutTeam === 0 || enhancedYutTeam === 1) {
        setTeams((teams) => teams.map((team, index) => (
          index === enhancedYutTeam ? { ...team, enhancedYut: true } : team
        )))
      }
      applyMinigameState(null)
    })

    socket.on("setAway", ({ player, paused }) => {
      setTeams((teams) => {
        const newTeams = [...teams] // make shallow copy
        const newPlayers = [...newTeams[player.team].players]; // make shallow copy of nested array
        const awayPlayerIndex = newPlayers.findIndex((currentPlayer) => currentPlayer.name === player.name)
        newPlayers[awayPlayerIndex].status = player.status
        newTeams[player.team] = {
          ...newTeams[player.team],
          players: newPlayers
        }
        return newTeams;
      })

      setClient((client) => {
        if (player.name === client.name && player.team === client.team) {
          const newClient = {
            ...client,
            status: player.status
          }
          return newClient
        }
        return client
      })

      setPauseGame(paused)
    })

    socket.on("setTeam", ({ user, prevTeam }) => {
      setSpectators((spectators) => {
        const newSpectators = [...spectators]
        if (prevTeam === -1) {
          const changingSpectatorIndex = newSpectators.findIndex((spectator) => spectator.name === user.name)
          newSpectators.splice(changingSpectatorIndex, 1);
        } else if (prevTeam === 0 || prevTeam === 1) {
          newSpectators.push(user);
        }
        return newSpectators
      })
      setTeams((teams) => {
        const newTeams = [...teams]
        if (prevTeam === -1) { // add to a team
          const newPlayers = [...newTeams[user.team].players];
          newPlayers.push(user)
          newTeams[user.team] = {
            ...newTeams[user.team],
            players: newPlayers
          }
        } else if (prevTeam === 0 || prevTeam === 1) { // remove from a team
          const newPlayers = [...newTeams[prevTeam].players];
          const changingPlayerIndex = newPlayers.findIndex((player) => player.name === user.name)
          newPlayers.splice(changingPlayerIndex, 1);

          newTeams[prevTeam] = {
            ...newTeams[prevTeam],
            players: newPlayers
          }
        }
        return newTeams
      })
      if (user.socketId === socket.id) {
        setClient(user)
      }
      setHost((host) => {
        if (user.socketId === host.socketId) {
          return user
        }
        return host
      })
    })

    socket.on("assignHost", ({ newHost }) => {
      if (newHost.team !== -1) {
        // use the setter to access the latest state.
        // if I use the one from the outside, players arrays are empty
        setTeams((teams) => { 
          const newHostIndex = teams[newHost.team].players.findIndex((player) => player.name === newHost.name)
          setHost(JSON.parse(JSON.stringify(teams[newHost.team].players[newHostIndex])))
          return teams
        })
      } else {
        setSpectators((spectators) => { 
          const newHostIndex = spectators.findIndex((spectator) => spectator.name === newHost.name)
          setHost(JSON.parse(JSON.stringify(spectators[newHostIndex])))
          return spectators
        })
      }
    })

    socket.on("kick", ({ team, name, turn, paused, gamePhase, hasAI }) => {
      if (team === -1) {
        // use the setter to access the latest state.
        // if I use the one from the outside, players arrays are empty
        setSpectators((spectators) => {
          const newSpectators = [...spectators]
          const changingSpectatorIndex = newSpectators.findIndex((spectator) => spectator.name === name)
          newSpectators.splice(changingSpectatorIndex, 1);
          return newSpectators
        })
      } else if (team === 0 || team === 1) {
        setTeams((teams) => {
          const newTeams = [...teams]
          const newPlayers = [...newTeams[team].players];
          const changingPlayerIndex = newPlayers.findIndex((player) => player.name === name)
          newPlayers.splice(changingPlayerIndex, 1);
          newTeams[team] = {
            ...newTeams[team],
            players: newPlayers
          }
          // check if you have the turn
          // setHasTurn(clientHasTurn(socket.id, teams, turn))
          return newTeams
        })
      }
      setTurn(turn)
      setPauseGame(paused)
      if (gamePhase === 'pregame' || gamePhase === 'game') {
        setHasAI(hasAI)
      }
    })

    socket.on("pause", ({ flag, turnStartTime, turnExpireTime }) => {
      setPauseGame(flag)
      setTurnStartTime(turnStartTime)
      setTurnExpireTime(turnExpireTime)
      setRemainingTime(turnExpireTime - Date.now())
    })

    socket.on("spectatorDisconnect", ({ name }) => {
      setSpectators((spectators) => {
        let removeSpectatorIndex = spectators.find((spectator) => spectator.name === name)
        spectators.splice(removeSpectatorIndex, 1)
        return spectators
      })
    })

    socket.on("playerDisconnect", ({ team, name }) => {
      setTeams((teams) => {
        let newTeams = [...teams]
        let disconnectPlayerIndex = newTeams[team].players.findIndex((player) => player.name === name)
        if (disconnectPlayerIndex !== -1) {
          newTeams[team].players[disconnectPlayerIndex].connectedToRoom = false
        }
        return newTeams
      });
    })

    socket.on("playerLeave", ({ team, userId }) => {
      setTeams((teams) => teams.map((teamData, teamIndex) => (
        teamIndex === team
          ? { ...teamData, players: teamData.players.filter((player) => player._id !== userId) }
          : teamData
      )));
    })

    socket.on("spectatorLeave", ({ userId }) => {
      setSpectators((spectators) => spectators.filter((spectator) => spectator._id !== userId));
    })

    socket.on("playerRoomSwitch", ({ roomPlayerIndex, roomPlayerTeam }) => {
      setTeams((teams) => {
        let newTeams = [...teams]
        newTeams[roomPlayerTeam].players.splice(roomPlayerIndex, 1)
        return newTeams
      });
    })

    socket.on("playerDisconnectLobby", ({ playersTeam0, playersTeam1 }) => {
      setTeams((teams) => {
        let newTeams = [...teams]
        newTeams[0].players = []
        for (const player of playersTeam0) {
          newTeams[0].players.push({
            ...player
          })
        }
        newTeams[1].players = []
        for (const player of playersTeam1) {
          newTeams[1].players.push({
            ...player
          })
        }
        return newTeams
      });
      
      if (playersTeam0.length > 0 && playersTeam1.length > 0 &&
        allPlayersConnected(playersTeam0, playersTeam1)) {
          setReadyToStart(true)
        } else {
          setReadyToStart(false)
        }
    })

    socket.on('kicked', () => {
      setSettingsOpen(false)
      socket.disconnect()
      localStorage.removeItem('yootGame')
    })

    socket.on('setGameRule', ({ rule, flag, turnStartTime, turnExpireTime, paused }) => {
      if (rule === 'backdoLaunch') {
        setBackdoLaunch(flag)
      } else if (rule === 'timer') {
        setTimer(flag)
        if (flag) {
          setTurnStartTime(turnStartTime)
          setRemainingTime(turnExpireTime - turnStartTime)
          setPauseGame(paused)
        }
        setTurnExpireTime(turnExpireTime)
      } else if (rule === 'nak') {
        setNak(flag)
      } else if (rule === 'yutMoCatch') {
        setYutMoCatch(flag)
      } else if (rule === 'shortcutOptions') {
        setShortcutOptions(flag)
      } else if (rule === 'minigame') {
        setMinigameRule(flag)
      }
    })

    socket.on('sendMessage', ({ name, team, text }) => {
      setMessages((messages) => {
        const newMessage = { name, team, text }
        return [...messages, newMessage]
      })
    })

    socket.on('disconnect', () => {
      console.log('[SocketManager][disconnect]')
      setIsOnline(false)
      setSettingsOpen(false)
      // clear animations
      setAlerts([])
      setYootAnimation(null)
    })

    return () => {
      // Remove only SocketManager's listeners, not all listeners
      socket.off('connect');
      socket.off('connect_error');
      socket.off('room');
      socket.off('teamNameUpdated');
      socket.off('playerNameUpdated');
      socket.off('throwYut');
      socket.off('gameStart');
      socket.off('passTurn');
      socket.off('recordThrow');
      socket.off('move');
      socket.off('score');
      socket.off('select');
      socket.off('joinRoom');
      socket.off('joinTeam');
      socket.off('reset');
      socket.off('setAway');
      socket.off('setTeam');
      socket.off('assignHost');
      socket.off('kick');
      socket.off('pause');
      socket.off('spectatorDisconnect');
      socket.off('playerDisconnect');
      socket.off('playerLeave');
      socket.off('spectatorLeave');
      socket.off('playerRoomSwitch');
      socket.off('playerDisconnectLobby');
      socket.off('kicked');
      socket.off('setGameRule');
      socket.off('sendMessage');
      socket.off('minigameState');
      socket.off('minigameStarPopped');
      socket.off('minigameHideAndSeekCatch');
      socket.off('minigameHideAndSeekShot');
      socket.off('minigameTime');
      socket.off('minigameStarsInitialized');
      socket.off('minigameReturnToGame');
      socket.off('disconnect');
      // leave socket.off('connect') so user can connect from Home2 again

      // Don't disconnect the socket - keep it alive for navigation back to Home2
      // disconnectSocket();
    }
  }, [])

  useEffect(() => {
    if (turn.team !== -1 && teams[0].players.length > 0 && teams[1].players.length > 0) {
      const currentTeam = turn.team
      const currentPlayer = turn.players[currentTeam]
      const currentPlayerName = teams[currentTeam].players[currentPlayer].name
      setCurrentPlayerName(currentPlayerName)
      setHasTurn(teams[currentTeam].players[currentPlayer].socketId === client.socketId)
    }
  }, [turn, teams, client])

  useEffect(() => {
    if (turn.team !== -1 && hasTurn && bonusExists) {
    // if (turn.team !== -1 && teams[0].players.length > 0 && teams[1].players.length > 0 && hasTurn && bonusExists) {
      setShowBonus(true)
    } else {
      setShowBonus(false)
    }
  }, [bonusExists, client, hasTurn])
};

/**
 * 
 * @param {array} players in team 0
 * @param {array} players in team 1
 * @returns boolean indicating whether all players are connected to the room
 */
function allPlayersConnected(playersTeam0, playersTeam1) {
  let flag = true;
  for (const player of playersTeam0) {
    if (!player.connectedToRoom) {
      flag = false
    }
  }
  for (const player of playersTeam1) {
    if (!player.connectedToRoom) {
      flag = false
    }
  }
  return flag
}